import { useContext, useEffect, useState } from 'react';
import {
  Button, Grid, TextField, MenuItem, TableContainer, Table,
  TableHead, TableBody, TableRow, TableCell, TableSortLabel,
  SvgIcon,
  Box,
  IconButton,
  useTheme,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Snackbar,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Copy01, Plus, SearchSm, Trash01 } from '../../../assets';
import { UserContext } from '../../../components/shared/useUser';

import { client, Order, OrderType, OrderlySystem, OrdersFilterOptions } from '../../../app/services/api/orderManagementClient';
import CustomPagination from '../../../components/forms/Pagination';
import LoadingWheel from '../../../components/ui/LoadingWheel';
import OrderStatusChip from '../../../components/shared/orders/OrderStatusChip';
import PageTitle from '../../../components/shared/PageTitle';
import PageArea from '../../../components/shared/PageArea';
import { useTranslation } from 'react-i18next';
import { OrderTypeTranslationMap, isOrderType } from '../../../enums/OrderType';
import dayjs from 'dayjs';
import DuplicateOrderModal from '../../../components/shared/orders/DuplicateOrderModal';
import OrderAuditModal from '../../../components/shared/orders/OrderAuditModal';

const OrdersList = () => {
  const navigate = useNavigate();
  const { selectedStore } = useContext(UserContext);
  const { t } = useTranslation('orderHistory');
  const { hasPermissionTo } = useContext(UserContext);
  const theme = useTheme();

  const [orderNumberSearch, setOrderNumberSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [orderType, setOrderType] = useState(-1);
  const [status, setStatus] = useState(-1);
  const [deliveryDateFrom, setDeliveryDateFrom] = useState('');
  const [deliveryDateTo, setDeliveryDateTo] = useState('');
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState<string>('OrderNumber');
  const [totalPages, setTotalPages] = useState(0);  
  const [orderTypes, setOrderTypes] = useState<OrderType[]>([]);

  const [orders, setOrders] = useState<Order[]>([]);

  const [isCopyOrderModalOpen, setIsCopyOrderModalOpen] = useState(false);
  const [isOrderAuditModalOpen, setIsOrderAuditModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);

  const handleSnackbarOpen = (message: string, severity: 'error' | 'warning' | 'info' | 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'warning' | 'info' | 'success';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState<number | null>(null);

  useEffect(() => {
    loadData();
  }, [page, selectedStore]);

  const loadData = async () => {
    setLoading(true);
    await fetchOrderTypes();
    await fetchOrders(getFilterOptions(false));
    setLoading(false);
  };

  const updateOrderEditStatus = (order: Order) => {
    return order.status;
  };

  const fetchOrders = async (filters: OrdersFilterOptions) => {
    try {
      const ordersResponse = await client.getOrders(filters);

      const updatedOrders = ordersResponse.data.items.map(o => ({
        ...o,
        status: updateOrderEditStatus(o),
      }));

      setOrders(updatedOrders);
      setTotalPages(ordersResponse.data.totalPages);
    } catch (error) {
      console.error(t('errorLoading'), error);
    }
  };

  const fetchOrderTypes = async () => {
    try {
      const orderTypesResponse = await client.getOrderTypes(selectedStore?.storeNumber);
      setOrderTypes(orderTypesResponse.data);
    } catch (error) {
      console.error(t('errorLoading'), error);
    }
  };

  const handleSort = async (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setLoading(true);
    await fetchOrders({
      orderNumber: orderNumberSearch,
      item: productSearch,
      orderType: orderType,
      status: status,
      deliveryDateFrom: deliveryDateFrom,
      deliveryDateTo: deliveryDateTo,
      storeNumber: selectedStore?.storeNumber,
      pageNumber: page,
      pageSize: pageSize,
      sort: property,
      direction: isAsc ? 'desc' : 'asc',
    });
    setLoading(false);
  };

  const handleSearch = async () => {
    setLoading(true);
    await fetchOrders(getFilterOptions(true));
    setLoading(false);
  };

  const handleClearFilters = async () => {
    setLoading(true);
    await fetchOrders({
      orderNumber: '',
      item: '',
      orderType: -1,
      status: -1,
      deliveryDateFrom: '',
      deliveryDateTo: '',
      storeNumber: selectedStore?.storeNumber,
      pageNumber: 1,
      pageSize: 10,
      sort: 'OrderNumber',
      direction: 'desc',
    });
    setPage(0);
    setPageSize(10);
    setOrderNumberSearch('');
    setProductSearch('');
    setOrderType(-1);
    setStatus(-1);
    setDeliveryDateFrom('');
    setDeliveryDateTo('');
    setLoading(false);
  };

  const getFilterOptions = (resetPage: boolean) => {
    const filterOptions: OrdersFilterOptions = {
      storeNumber: selectedStore?.storeNumber,
      orderNumber: orderNumberSearch,
      item: productSearch,
      orderType: orderType,
      status: status,
      deliveryDateFrom: deliveryDateFrom,
      deliveryDateTo: deliveryDateTo,
      pageNumber: resetPage ? 0 : page,
      pageSize: pageSize,
      sort: orderBy,
      direction: order,
    };
    return filterOptions;
  };

  const handleOpenCopyModal = (event: React.MouseEvent, orderId: number) => {
    event.stopPropagation();
    setSelectedOrderId(orderId);
    setIsCopyOrderModalOpen(true);
  };

  const handleOpenAuditModal = (event: React.MouseEvent, orderId: number) => {
    event.stopPropagation();
    setSelectedOrderId(orderId);
    setIsOrderAuditModalOpen(true);
  };
  const handleOrderCopyConfirm = async () => {
    setIsCopyOrderModalOpen(false);
    setLoading(true);
    try {
      const result = await client.duplicateOrder(selectedStore?.storeNumber, selectedOrderId!);
      if (result.success) {
        handleSnackbarOpen(t('duplicateSuccess', 'Order duplicated successfully'), 'success');
        await loadData();
      } else {
        handleSnackbarOpen(t('duplicateError', 'Failed to duplicate order'), 'error');
      }
    } catch (error) {
      handleSnackbarOpen(t('duplicateError', 'Failed to duplicate order') + ' ' + error, 'error');
    } finally {
      setLoading(false);
      setSelectedOrderId(null);
    }
  };

  const handleDeleteOrder = async (event: React.MouseEvent, orderId: number) => {
    event.stopPropagation();
    setOrderToDelete(orderId);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteOrder = async () => {
    if (!orderToDelete) return;

    setLoading(true);
    try {
      const response = await client.deleteOrder(selectedStore?.storeNumber, orderToDelete.toString());
      if (response.success) {
        handleSnackbarOpen(t('orderDeletedSuccessfully', 'Order deleted successfully'), 'success');
        loadData();
      } else {
        handleSnackbarOpen(response.message, 'error');
      }
    } catch (error) {
      console.error(t('errorDeletingOrder', 'Error deleting order'), error);
      handleSnackbarOpen(t('errorDeletingOrder', 'Error deleting order'), 'error');
    } finally {
      setDeleteDialogOpen(false);
      setOrderToDelete(null);
      setLoading(false);
    }
  };

  const cancelDeleteOrder = () => {
    setDeleteDialogOpen(false);
    setOrderToDelete(null);
  };

  const createNewOrder = async (orderTypeId: number) => {
    if (!selectedStore?.storeNumber) return;

    const today = dayjs().format('YYYY-MM-DD');

    const createOrderRequest = {
      storeNumber: selectedStore.storeNumber,
      emergencyOrder: true,
      orderTypeId: orderTypeId,
      deliveryDate: today,
      cutOffDate: today,
      supplierNumber: '',
      orderlySystem: OrderlySystem.OFD,
      items: [],
    };

    try {
      setLoading(true);
      const response = await client.createOrder(createOrderRequest);
      setLoading(false);      
      if (response.success) {
        handleSnackbarOpen(t('orderCreatedSuccessfully'), 'success');
        navigate(`/products-list/${response.data}`);
      }
      else {
        handleSnackbarOpen(response.message, 'error');
      }
    } catch (error) {
      console.error('Failed to create order:', error);
      setLoading(false);
    }
  };

  const isOrderEditable = (order: Order): boolean => {
    return order.status === 'Pending' || order.status === 'Editable';
  };

  return (
    <PageArea>
      <Box>
        <Grid item
          xs={12}
          sx={{ textAlign: 'left' }}>
          <PageTitle subTitle={'View existing orders or add a new one'}>{'Orders'}</PageTitle>

        </Grid>
        <Grid item
          xs={12}
          container
          rowGap={5}
          columnGap={5}
          justifyContent="flex-end">
          <Button variant="primary"
            size="lg"
            startIcon={<SvgIcon> <Plus fill='white' /> </SvgIcon>}
            sx={{ ml: { sm: 2 } }}
            disabled={!hasPermissionTo(['OrdersCreate'])}
            onClick={() => createNewOrder(27)}
          >
            {t('newAmbient')}
          </Button>
          <Button variant="primary"
            size="lg"
            startIcon={<SvgIcon> <Plus fill='white' /> </SvgIcon>}
            sx={{ ml: { sm: 2 } }}
            disabled={!hasPermissionTo(['OrdersCreate'])}
            onClick={() => createNewOrder(101)}
          >
            {t('newFrozen')}
          </Button>
        </Grid>
        <Box mt={5}>
          <Grid item
            container
            spacing={6}
            p={3}>
            <Grid item
              lg={2.4}
              xs={12}>
              <TextField
                fullWidth
                size='small'
                label={t('searchbyOrderNumber')}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    handleSearch();
                  }
                }}
                value={orderNumberSearch}
                onChange={(e) => setOrderNumberSearch(e.target.value)}
              />
            </Grid>
            <Grid item
              lg={2.4}
              xs={12}>
              <TextField
                fullWidth
                size='small'
                label={t('orderType')}
                value={orderType}
                required
                onChange={(e) => setOrderType(Number(e.target.value))}
                select
              >
                <MenuItem value="-1">{t('ordersType.all')}</MenuItem>
                {orderTypes.map((type) => (
                  <MenuItem key={type.id}
                    value={type.id}>
                    {isOrderType(type.id.toString()) ? t(OrderTypeTranslationMap[type.name as keyof typeof OrderTypeTranslationMap]) : type.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item
              lg={2.4}
              xs={12}>
              <TextField
                fullWidth
                size='small'
                label={t('status')}
                value={status}
                required
                onChange={(e) => setStatus(Number(e.target.value))}
                select
              >
                <MenuItem value="-1">{t('all')}</MenuItem>
                <MenuItem value="0">{'Pending'}</MenuItem>
                <MenuItem value="18">{'Review'}</MenuItem>
                <MenuItem value="14">{'Approved'}</MenuItem>
                <MenuItem value="19">{'Amended'}</MenuItem>
                <MenuItem value="1">{'Published'}</MenuItem>
                <MenuItem value="6">{'Order Shipped'}</MenuItem>
                <MenuItem value="16">{'Cancelled'}</MenuItem>
              </TextField>
            </Grid>
            <Grid item
              lg={2.4}
              xs={12}>
              <Button
                fullWidth
                variant="secondary"
                size="lg"
                onClick={handleClearFilters}
              >
                {t('clearFilters')}
              </Button>
            </Grid>
            <Grid item
              lg={2.4}
              xs={12}>
              <Button
                fullWidth
                variant="primary"
                size="lg"
                startIcon={<SearchSm />}
                onClick={handleSearch}
              >
                {t('search')}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
      {loading ? <LoadingWheel /> :
        <TableContainer sx={{ borderRadius: '12px' }}
          component={'div'}>
          <Table
            size="small"
            aria-label="a table"
          >
            <TableHead
              sx={{
                paddingTop: '10px',
                backgroundColor: theme.palette.custom.gray[200],
              }}>
              <TableRow>
                <TableCell
                  sx={{ fontWeight: 'bold' }}
                  align="left">
                  <TableSortLabel
                    active={orderBy === 'OrderNumber'}
                    direction={order}
                    onClick={() => handleSort('OrderNumber')}>
                    {t('orderNumber')}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 'bold' }}
                  align="left">
                  <TableSortLabel
                    active={orderBy === 'Status'}
                    direction={order}
                    onClick={() => handleSort('Status')}>
                    {t('status')}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 'bold' }}
                  align="left">
                  <TableSortLabel
                    active={orderBy === 'OrderType'}
                    direction={order}
                    onClick={() => handleSort('OrderType')}>
                    {t('orderType')}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 'bold' }}
                  align="left">
                  <TableSortLabel
                    active={orderBy === 'DeliveryDate'}
                    direction={order}
                    onClick={() => handleSort('DeliveryDate')}>
                    {t('deliveryDate')}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 'bold' }}
                  align="center">
                  <TableSortLabel
                    active={orderBy === 'TotalQuantity'}
                    direction={order}
                    onClick={() => handleSort('TotalQuantity')}>
                    {t('totalQuantity')}
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  sx={{ fontWeight: 'bold' }}
                  align="center">
                  {t('actions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <TableRow
                  key={order.id}
                  hover
                  onClick={() => navigate(`/order/${order.id}`)}
                >
                  <TableCell>
                    <Grid container
                      alignItems="center">
                      <Grid item>
                        {order.id}
                      </Grid>
                    </Grid>
                  </TableCell>
                  <TableCell>
                    <Grid container
                      alignItems="center">
                      <Grid item>
                        <OrderStatusChip orderStatus={order.status} />
                      </Grid>
                    </Grid>
                  </TableCell>
                  <TableCell>
                    {order.orderTypeId === 1 ? 'Ambient' : isOrderType(order.orderType) ? t(OrderTypeTranslationMap[order.orderType]) : order.orderType}
                  </TableCell>
                  <TableCell>{order.deliveryDate}</TableCell>
                  <TableCell align="center">{order.totalQuantity}</TableCell>
                  <TableCell
                    align="center"
                    sx={{ p: 0, alignContent: 'center' }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Tooltip title={t('audit', 'View Audit')}>
                        <IconButton
                          size='small'
                          onClick={(event) => handleOpenAuditModal(event, order.id)}
                        >
                          <SvgIcon style={{ color: theme.palette.primary[700] }}><SearchSm /></SvgIcon>
                        </IconButton>
                      </Tooltip>                      
                      <Tooltip title={t('copyOrder', 'Copy Order')}>
                        <span>
                          <IconButton
                            size='small'
                            disabled={!hasPermissionTo(['OrdersCopy'])}
                            onClick={(event) => handleOpenCopyModal(event, order.id)}
                          >
                            <SvgIcon color={'info'}><Copy01 /></SvgIcon>
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={isOrderEditable(order) ? t('deleteOrder', 'Delete Order') : t('cannotDeleteOrder', 'Cannot delete this order')}>
                        <span>
                          <IconButton
                            size='small'
                            onClick={(event) => handleDeleteOrder(event, order.id)}
                            disabled={!isOrderEditable(order)}
                          >
                            <SvgIcon color={isOrderEditable(order) ? 'error' : 'disabled'}><Trash01 /></SvgIcon>
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <CustomPagination
            page={page}
            setPage={setPage}
            maxPages={totalPages}
            breakpointForChangeDisplay={120000}
          />
        </TableContainer>
      }      
      <DuplicateOrderModal
        currentOrderId={selectedOrderId ?? 0}
        open={isCopyOrderModalOpen}
        onClose={() => {
          setIsCopyOrderModalOpen(false);
          setSelectedOrderId(null);
        }}
        onConfirm={handleOrderCopyConfirm}
      />

      <OrderAuditModal
        isOfd={true}
        currentOrderId={selectedOrderId ?? 0}
        open={isOrderAuditModalOpen}
        onClose={() => setIsOrderAuditModalOpen(false)}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDeleteOrder}
      >
        <DialogTitle>{t('deleteOrder', 'Delete Order')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('confirmDeleteOrder', 'Are you sure you want to delete this order?')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDeleteOrder}
            variant="secondary">
            {t('cancel', 'Cancel')}
          </Button>
          <Button onClick={confirmDeleteOrder}
            variant="bad"
            autoFocus>
            {t('confirm', 'Confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageArea>
  );
};

export default OrdersList;
